import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import Message from './Message';
import Typing from './Typing.jsx';
import { animateEaseOut } from 'services/animation';
import GeckoLocation from 'services/gecko-location';
import LoadMore from 'components/LoadMore';
import { GeckoAvatar } from 'ui';
import ReplyBoxWrapper from './ReplyBoxWrapper';
import { fetchContact } from '../actions';
import GeckoRequest from 'services/gecko-request';
import ConversationHeader from './ConversationHeader';
import GeckoSocket from 'services/gecko-socket';
import CONSTANTS from 'chat-constants';

import './ConversationContainer.scss';

const joinConversationRoom = (conversationId) =>
  GeckoSocket.emit('join-conversation-messages-room', { conversationId });
const leaveConversationRoom = (conversationId) =>
  GeckoSocket.emit('leave-conversation-messages-room', { conversationId });

class ConversationContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      addingTag: false,
      search: '',
      results: [],
      hasSearched: false,
      userTyping: false,
    };
    this.messagesRefs = {};
    this.oldMessagesRefs = {};
    this.messagesEnd = null;
  }

  componentDidMount() {
    if (GeckoLocation.params.message) {
      const index = this.props.messages.findIndex(
        (m) => Number(m.id) === Number(GeckoLocation.params.message)
      );
      this.scrollToMessage(index);
    } else {
      this.scrollToBottom();
    }
    if (!this.props.contact && !this.props.fetchingContact) {
      fetchContact(this.props.contactId);
    }
    joinConversationRoom(this.props.conversation.id);
  }

  componentDidUpdate(prevProps, _prevState, prevContext) {
    if (prevProps.messages.length !== this.props.messages.length) {
      this.scrollToBottom();
    }
    if (!this.props.contact && !this.props.fetchingContact) {
      fetchContact(this.props.contact_id);
    }
    if (this.props.conversation.id !== prevProps.conversation.id) {
      leaveConversationRoom(prevProps.conversation.id);
      joinConversationRoom(this.props.conversation.id);
    }
  }

  componentWillUnmount() {
    leaveConversationRoom(this.props.conversation.id);
  }

  scrollToBottom() {
    this.messagesEnd.scrollIntoView();
  }

  scrollToMessage(messageIndex) {
    if (
      this.messagesRefs[messageIndex]?.current &&
      ReactDOM.findDOMNode(this.messagesRefs[messageIndex].current)
    ) {
      animateEaseOut(
        {
          timeout: 10,
          from: this.scrollableMessages.scrollTop,
          to: ReactDOM.findDOMNode(this.messagesRefs[messageIndex].current)
            .offsetTop,
        },
        (newPosition) => {
          if (this.scrollableMessages) {
            this.scrollableMessages.scrollTop = newPosition;
          }
        }
      );
    }
  }

  goToMessage(message) {
    if (message) {
      const index = this.props.messages.findIndex(
        (m) => Number(m.id) === Number(message.id)
      );
      if (index > -1) {
        this.scrollToMessage(index);
      }
    }
  }

  renderUserTyping() {
    const users = this.props.typingStatus.filter((u) => u.type === 'user');
    return users.map((user) => {
      return (
        <div className="row" key={user.id}>
          <div className="conversation-message-avatar col-1">
            <GeckoAvatar
              name={user.full_name}
              first_name={user.first_name}
              last_name={user.last_name}
              color={user.additional.avatar_colour}
              bgImage={user.additional.profile_picture_url}
            />
          </div>

          <div className="col-10">
            <div style={{ paddingLeft: '20px' }}>
              <Typing />
            </div>
          </div>
        </div>
      );
    });
  }

  renderAgentTyping() {
    const agents = this.props.typingStatus.filter((u) => u.type === 'agent');
    return agents.map((agent) => {
      return (
        <div className="ConversationMessage agent" key={agent.id}>
          <div className="row">
            <div className="conversation-message-avatar">
              <GeckoAvatar
                name={agent.full_name}
                first_name={agent.first_name}
                last_name={agent.last_name}
                color={agent.additional.avatar_colour}
                bgImage={agent.additional.profile_picture_url}
              />
            </div>

            <div className="conversation-message-container col-10">
              <Typing />
            </div>
          </div>
        </div>
      );
    });
  }

  showConversation() {
    if (!this.props.conversation) {
      return <div></div>;
    }

    const channelsInConvo = this.props.messages.map((m) => m.sender_channel);
    const availableChannels = this.props.channels.filter((channel) => {
      if (CONSTANTS.CHANNEL.UNPICKABLE_TYPES.includes(channel.type))
        return false;
      if (
        this.props.contact &&
        this.props.contact.tel &&
        channel.type === CONSTANTS.CHANNEL.TYPE_TWILIO
      )
        return true;
      if (
        this.props.contact &&
        this.props.contact.email &&
        channel.type === CONSTANTS.CHANNEL.TYPE_MAILGUN
      )
        return true;
      return (
        channelsInConvo.find((c) => c && c.id === channel.id) !== undefined
      );
    });

    const senderChannelId = this.props.channels.find(
      (c) => c.type === 'gecko_chat'
    ).id;
    return (
      <div className="ConversationContainer-wrapper">
        {this.getConversationHeader()}
        <div
          className="conversation-messages-container"
          ref={(elem) => (this.scrollableMessages = elem)}
        >
          <LoadMore
            loadItems={() =>
              GeckoRequest.get(
                `/message/conversation/${this.props.conversation.id}?page=${
                  this.props.messagesPagination.page + 1
                }`
              )
            }
            onNewItems={({ messages }) => {
              const data = messages.data;
              const messageList = data.map((message) => {
                return Object.assign({}, message, {
                  recipient_channel: message.RecipientChannel,
                  sender_channel: message.SenderChannel,
                  sender: message.Sender,
                });
              });
              const pagination = messages.pagination;
              this.props.handleNewlyLoadedItems(messageList, pagination);
              this.scrollToMessage(data.length - 1);
            }}
            moreToLoad={
              this.props.messages.length < this.props.messagesPagination.total
            }
          />
          {this.props.messages.map((message, index) => {
            const ref = React.createRef();
            this.messagesRefs[index] = ref;
            return (
              <Message
                ref={ref}
                key={message.id}
                message={message}
                availableChannels={availableChannels}
                contact={this.props.contact}
              />
            );
          })}
          <div
            style={{ height: '1px' }}
            ref={(el) => (this.messagesEnd = el)}
          ></div>
          {this.renderUserTyping()}
          {this.renderAgentTyping()}
        </div>
        {this.getReplyBox(senderChannelId, availableChannels)}
      </div>
    );
  }

  updateCC(cc) {
    return GeckoRequest.post(
      `/conversation/${this.props.conversation.id}/update-cc`,
      { cc }
    );
  }

  updateBCC(bcc) {
    return GeckoRequest.post(
      `/conversation/${this.props.conversation.id}/update-bcc`,
      { bcc }
    );
  }

  getConversationHeader() {
    if (!(this.props.conversation.id && this.props.conversation.messages)) {
      return null;
    }

    return (
      <ConversationHeader
        changeConversationStatus={this.props.changeConversationStatus}
        conversation={this.props.conversation}
        deleteConversation={this.props.deleteConversation}
        toggleContactDetails={this.props.toggleContactDetails}
        contactDetailsOpen={this.props.contactDetailsOpen}
      ></ConversationHeader>
    );
  }

  getReplyBox() {
    if (!(this.props.conversation.id && this.props.conversation.messages)) {
      return null;
    }
    // removed this prop as it doesn't appear to be required, and it triggers a re-render when called:
    // handleClick={this.props.handleClick.bind(this)}
    return (
      <ReplyBoxWrapper
        contact={this.props.contact}
        conversationId={this.props.conversation.id}
        currentFilter={this.props.currentFilter}
        conversationAgentIds={this.props.conversation.agents
          .filter((a) => a != null)
          .map((a) => Number(a.id))}
        messages={this.props.conversation.messages}
        scrollToBottom={this.scrollToBottom.bind(this)}
        updateAssignedConversationState={
          this.props.updateAssignedConversationState
        }
        updateMessagesState={this.props.updateMessagesState}
        conversationCC={this.props.conversation.cc || []}
        conversationBCC={this.props.conversation.bcc || []}
        updateCC={this.updateCC.bind(this)}
        updateBCC={this.updateBCC.bind(this)}
      />
    );
  }

  // If this comment is removed the app will blow up

  render() {
    return (
      <div className="ConversationContainer">
        <div className="card messageFeedCard">
          <div className="card-block conversation-messages">
            {this.showConversation()}
          </div>
        </div>
      </div>
    );
  }
}

ConversationContainer.contextTypes = {
  location: PropTypes.object,
};

ConversationContainer.defaultProps = {
  channels: [],
  messages: [],
  senders: [],
  tags: [],
  agentIds: [],
};

ConversationContainer.propTypes = {
  channels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    })
  ),
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
    })
  ),
  senders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    })
  ),
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    })
  ),
  conversation: PropTypes.object,
  agentIds: PropTypes.array,
  agent: PropTypes.object,
};

const mapStateToProps = (state, props) => {
  return {
    contact: state.conversationsRoute.contactsById[props.conversation.user_id],
    typingStatus: state.typing_status[props.conversation.id] || [],
  };
};

export default connect(mapStateToProps)(ConversationContainer);
